import * as React from 'react';

import {useSelector, useDispatch} from 'react-redux';
import useI18n from 'src/hooks/useI18n';

import {selectApplicantInformation, selectUserInfo} from 'src/selectors/crm';

import {
  Card,
  CardHeader,
  CardTitle,
  CardActions,
  CardContent,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {ButtonDropdown} from '@spaced-out/ui-design-system/lib/components/ButtonDropdown';
import {
  FormLabelSmall,
  ButtonTextExtraSmall,
  BodyMedium,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {getApplicantInformation} from 'src/action-creators/crm';
import {
  Toast,
  TOAST_SEMANTIC,
  type ToastSemanticType,
  ToastBody,
  ToastFooter,
  ToastTitle,
  toastApi,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {useHistory} from 'src/rerouter';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import {sizeFluid} from '@spaced-out/ui-design-system/lib/styles/variables/_size';

import {useCopyToClipboard} from '@spaced-out/ui-design-system/lib/hooks/useCopyToClipboard';
import css from './resume.css';

import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {getBaseUrl, getApiPrefix} from 'src/utils/crm';


const ResumeCard = ({
  applicantId,
  jobId,
}: {
  applicantId: number;
  jobId?: string;
}) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const router = useHistory();
  const dispatch = useDispatch();
  const {user: userInfo} = useSelector(selectUserInfo) ?? {};
  const [isLoading, setIsLoading] = React.useState(true);
  const applicantDetails = useSelector(selectApplicantInformation);

  const resumeData = applicantDetails?.resume;
  const [copiedText, copy] = useCopyToClipboard();

  React.useEffect(() => {
    if (!applicantDetails || applicantDetails.id !== applicantId) {
      setIsLoading(true);
      if (applicantId) {
        dispatch(getApplicantInformation(applicantId)).finally(() =>
          setIsLoading(false),
        );
      } else {
        console.error('Unable to find applicant');
      }
    } else {
      setIsLoading(false);
    }
  }, [dispatch, applicantId]);

  const hasVersionHistory =
    applicantDetails?.candidateAttachments?.some(
      (attachment: any) => attachment.attachmentType === 'RESUME',
    ) ?? false;

  const updatedDate = React.useMemo(() => {
    if (!resumeData?.updatedOn) {
      return '';
    }

    try {
      const date = new Date(resumeData.updatedOn);
      if (isNaN(date.getTime())) {
        return '';
      }
      return date.toISOString().split('T')[0];
    } catch (e: any) {
      return '';
    }
  }, [resumeData?.updatedOn]);

  const showToastMessage = (message: string, type: ToastSemanticType) => {
    const toastJsx = (
      <Toast semantic={type}>
        <ToastTitle>{message} </ToastTitle>
      </Toast>
    );
    toastApi.show(toastJsx, {
      autoClose: true,
    });
  };
  const handleDownloadResume = async () => {
    const originalResumeURI = window.encodeURI(
      `${getApiPrefix(
        userInfo?.roleTitle ?? '',
      )}/api/candidate/download-original-resume/${applicantDetails.id}/${
        applicantDetails.name ||
        // @ts-expect-error - TS2339 - Property 'resumeFilepath' does not exist on type 'Resume'.
        resumeData.resumeFilepath ||
        // @ts-expect-error - TS2339 - Property 'resumeFilekey' does not exist on type 'Resume'.
        resumeData.resumeFilekey
      }?sendPDF=true`,
    );
    window.open(originalResumeURI, '_blank');
  };

  const viewResumeHistory = () => {
    if (jobId) {
      router.push(
        `/crm/job/${jobId}/info/applicants?applicantId=${applicantId}&selectedTab=resume&showResumeHistory=true`,
      );
    } else {
      router.push(
        `/crm/candidates/${applicantId}?selectedTab=resume&showResumeHistory=true`,
      );
    }
  };

  const resumeLink = React.useMemo(() => {
    if (!applicantDetails) {
      return '';
    }
    const baseUrl = getBaseUrl(window.location.href);
    if (!baseUrl) {
      return '';
    }
    // @ts-expect-error - TS2551 - Property 'resumeFileKey' does not exist on type 'Candidate'. Did you mean 'resumeFilekey'?
    const resumeUrl = `${baseUrl}/trmapi/api/candidate/show-pdf-resume/${applicantDetails.id}/${applicantDetails.resumeFileKey}`;
    return resumeUrl;
  }, [applicantDetails]);

  if (isLoading) {
    return <CircularLoader className={css.loader} />;
  }
  const handleDownloadOriginalResume = () => {
    const originalResumeURI = window.encodeURI(
      `${getApiPrefix(
        userInfo?.roleTitle ?? '',
      )}/api/candidate/download-original-resume/${applicantDetails.id}/${
        applicantDetails.name ||
        // @ts-expect-error - TS2339 - Property 'resumeFilepath' does not exist on type 'Resume'.
        resumeData.resumeFilepath ||
        // @ts-expect-error - TS2339 - Property 'resumeFilekey' does not exist on type 'Resume'.
        resumeData.resumeFilekey
      }?sendPDF=true`,
    );
    const link = document.createElement('a');
    link.href = originalResumeURI;
    link.setAttribute('download', ``);
    document.body?.appendChild(link);
    link.click();
    link.remove();
  };

  const handleDownloadPdf = async () => {
    const originalResumeURI = window.encodeURI(
      `${getApiPrefix(
        userInfo?.roleTitle ?? '',
      )}/api/candidate/download-original-resume/${applicantDetails.id}/${
        applicantDetails.name ||
        // @ts-expect-error - TS2339 - Property 'resumeFilepath' does not exist on type 'Resume'.
        resumeData.resumeFilepath ||
        // @ts-expect-error - TS2339 - Property 'resumeFilekey' does not exist on type 'Resume'.
        resumeData.resumeFilekey
      }?sendPDF=true`,
    );
    window.open(originalResumeURI, '_blank');
  };

  return (
    <div className={css.card}>
      <div className={css.cardContent}>
        <div className={css.contentHeader}>
          {hasVersionHistory ? (
            <>
              <div className={css.headerLabel}>
                <FormLabelSmall>
                  {`${t('Last_updated_on', 'Last updated on')} ${updatedDate}`}
                </FormLabelSmall>
              </div>

              <div>
                <ButtonDropdown
                  children={t('Download', 'Download')}
                  menu={{
                    options: [
                      {
                        key: 'downloadPdf',
                        label: t('Download_PDF', 'Download PDF'),
                      },
                      {
                        key: 'downloadOriginalResume',
                        label: t('Download_Original', 'Download Original'),
                      },
                    ],
                    size: 'small',
                    classNames: {
                      option: css.buttonDropdownOption,
                    },
                  }}
                  anchorPosition={`bottom-end`}
                  onOptionSelect={(option) => {
                    if (option.key === 'downloadPdf') {
                      handleDownloadPdf();
                    }
                    if (option.key === 'downloadOriginalResume') {
                      handleDownloadOriginalResume();
                    }
                  }}
                  size="small"
                  type="tertiary"
                />
              </div>
            </>
          ) : null}
        </div>
        <div className={css.resumeContainer}>
          {resumeLink ? (
            <div className={css.iframe}>
              <iframe
                title="resume-iframe"
                src={resumeLink}
                height={sizeFluid}
                width={sizeFluid}
              />
            </div>
          ) : (
            <EmptyState
              description={t(
                'No_resume_found_for_this_candidate',
                'No resume found for this candidate',
              )}
              imageVariant="data"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ResumeCard;
